import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { unitTypesService, type UnitTypeQueryParams } from '../../services/unit-types.service';
import { masterService } from '../../services/master.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { UnitType, Department } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'name', header: 'Unit Type Name', filter: true }];

const UnitTypesList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'unit-types',
    basePath: '/unit-types',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.UNIT_TYPE) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.UNIT_TYPE);
  const canDelete = useCanDelete(JOB_NAMES.UNIT_TYPE);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  // Departments map for displaying department names
  const [departmentMap, setDepartmentMap] = useState<Map<string, string>>(new Map());

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  // Load departments once on mount
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const departments = await masterService.getDepartments();
        setDepartmentMap(new Map(departments.map((d: Department) => [d._id, d.name])));
      } catch (e) {
        console.warn('Failed to load departments', e);
      }
    };
    loadDepartments();
  }, []);

  const fetchUnitTypes = useCallback(async (
    page: number = 1,
    size: number = pageSize,
    sortBy: string | null = sortField,
    order: 1 | -1 | null = sortOrder,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);
      const params: UnitTypeQueryParams = {
        page,
        page_size: size,
        include_deleted: includeDeleted,
      };

      if (debouncedSearchText && debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim();
      }

      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }

      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      if (selectedProjectId) params.app_id = selectedProjectId;
      params.include_global = showCore;

      const response = await unitTypesService.getAll(params);
      const mappedData = (response.data || []).map((item: any) => ({
        ...item,
        id: item._id || item.id,
      }));
      setUnitTypes(mappedData);

      // Handle both totalItems and total from API response
      const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
      setTotalRecords(total);
    } catch (err: any) {
      console.error('Failed to fetch unit types:', err);
      showError(extractErrorMessage(err, 'Failed to load unit types. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]);

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchUnitTypes(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort event - server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;

    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Reset to first page when sorting changes
    setFirst(0);
    fetchUnitTypes(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchUnitTypes(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchUnitTypes(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDepartmentName = (departmentId?: string) => {
    if (!departmentId) return '-';
    return departmentMap.get(departmentId) ?? departmentId;
  };

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;

    try {
      setDeleteLoading(true);
      await unitTypesService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchUnitTypes(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete unit type:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete unit type'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const columns: SaasColumn<UnitType>[] = useMemo(() => {
    const cols: SaasColumn<UnitType>[] = [
      { field: 'name', header: 'Unit Type Name', sortable: true, filter: true, hideable: false },
      {
        field: 'departmentId',
        header: 'Department',
        body: (rowData) => getDepartmentName(rowData.departmentId),
      },
      { field: 'level', header: 'Level' },
    ];

    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (rowData) => {
          const deleted = (rowData as any).isDelete || rowData.isDeleted;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData) => {
        const deleted = (rowData as any).isDelete || rowData.isDeleted;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleView(rowData._id)}
              data-testid="UnitTypesList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    onClick={() => handleEdit(rowData._id)}
                    data-testid="UnitTypesList.Action.Edit"
                    data-pr-tooltip="Edit"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    onClick={() => handleDeleteClick(rowData._id)}
                    data-testid="UnitTypesList.Action.Delete"
                    data-pr-tooltip="Delete"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [departmentMap, canUpdate, canDelete, canManageProjects, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.UNIT_TYPE}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-UnitTypes-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-sitemap"
          title="Unit Types"
          subtitle="Manage unit types and their department associations"
          testId="UnitTypes.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="unit-types"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={unitTypes}
            loading={loading}
            dataKey="_id"
            emptyMessage="No unit types found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search unit types..."
            testId="UnitTypesList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="UnitTypesList.Filter.Project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="UnitType.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchUnitTypes(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="Create Unit Type"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="UnitTypesList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this unit type? This will mark it as deleted."
          testId="UnitTypesList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default UnitTypesList;
